import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import { 
getCategoriesNameById,
getFoodTypeById,
formatCreatedAtDate,
formatNumberWithComma
} from '../../utils/helpers';

// Export to CSV with all attributes (using direct download)
export const exportToCSV = async (data, referenceData) => {
try {
    const fileName = `food_items_${new Date().toISOString().split('T')[0]}.csv`;
    
    const csvData = data.map(item => ({
    'Date': formatCreatedAtDate(item.created_at),
    'Food Name': item.name,
    'Type': getFoodTypeById(item.food_type_id) || 'N/A',
    'Category': getCategoriesNameById(item.food_category_id, referenceData?.categoryNames || []) || 'N/A',
    'Price': `$${formatNumberWithComma(item.price)}`,
    'Description': item.description || '',
    'Ingredients Count': item.ingredients?.length || 0,
    'Status': item.status === 1 ? 'Active' : 'Inactive',
    'Branch': getCategoriesNameById(item.branch_id, referenceData?.branchNames || []) || 'N/A'
    }));

    const csvHeaders = Object.keys(csvData[0]);
    const csvRows = csvData.map(row => 
    csvHeaders.map(header => {
        const value = row[header] || '';
        const escapedValue = String(value).replace(/"/g, '""');
        return escapedValue.includes(',') ? `"${escapedValue}"` : escapedValue;
    }).join(',')
    );
    
    const csvString = [csvHeaders.join(','), ...csvRows].join('\r\n');
    const BOM = '\uFEFF';
    const csvContent = BOM + csvString;
    
    // Create and download without FileSaver
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
    return Promise.resolve();
} catch (error) {
    return Promise.reject(error);
}
};

// Export to Excel with all attributes (using direct download)
export const exportToExcel = async (data, referenceData) => {
try {
    const fileExtension = '.xlsx';
    const fileName = `food_items_${new Date().toISOString().split('T')[0]}${fileExtension}`;
    
    const wsData = [
    ['Date', 'Food Name', 'Type', 'Category', 'Price', 'Description', 'Ingredients Count', 'Status', 'Branch'],
    ...data.map(item => [
        formatCreatedAtDate(item.created_at),
        item.name,
        getFoodTypeById(item.food_type_id) || 'N/A',
        getCategoriesNameById(item.food_category_id, referenceData?.categoryNames || []) || 'N/A',
        `$${formatNumberWithComma(item.price)}`,
        item.description || '',
        item.ingredients?.length || 0,
        item.status === 1 ? 'Active' : 'Inactive',
        getCategoriesNameById(item.branch_id, referenceData?.branchNames || []) || 'N/A'
    ])
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    
    // Set column widths
    const wscols = [
    { wch: 15 }, // Date
    { wch: 30 }, // Food Name
    { wch: 15 }, // Type
    { wch: 20 }, // Category
    { wch: 12 }, // Price
    { wch: 40 }, // Description
    { wch: 18 }, // Ingredients Count
    { wch: 12 }, // Status
    { wch: 20 }  // Branch
    ];
    ws['!cols'] = wscols;

    const wb = { Sheets: { 'Food Items': ws }, SheetNames: ['Food Items'] };
    
    // Use XLSX.writeFile to download directly
    XLSX.writeFile(wb, fileName);
    
    return Promise.resolve();
} catch (error) {
    return Promise.reject(error);
}
};

// Helper function to split text into multiple lines (like Shift service)
const splitTextIntoLines = (doc, text, maxWidth) => {
const words = text.split(' ');
const lines = [];
let currentLine = words[0];

for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = doc.getTextWidth(currentLine + ' ' + word);
    if (width < maxWidth) {
    currentLine += ' ' + word;
    } else {
    lines.push(currentLine);
    currentLine = word;
    }
}
lines.push(currentLine);
return lines;
};

// Manual table drawing function for PDF (like Shift service structure) - UPDATED FOR LANDSCAPE
const drawFoodTable = (doc, data, startY, referenceData) => {
const pageWidth = doc.internal.pageSize.width; // This will be longer in landscape
const pageHeight = doc.internal.pageSize.height; // This will be shorter in landscape
const margin = 20; // Increased margin for better spacing in landscape
const rowHeight = 12;

// Column widths for landscape mode - can be wider now
const colWidths = [30, 60, 30, 40, 30, 35, 50]; // Wider columns for landscape

let currentY = startY;
const neededHeight = (data.length + 1) * rowHeight;

// Check if we need a new page (using pageHeight for landscape)
if (currentY + neededHeight > pageHeight - 30) {
    doc.addPage('l'); // Add landscape page
    currentY = margin;
}

// Draw table header
doc.setFillColor(25, 118, 210); // Blue header
doc.setTextColor(255, 255, 255); // White text
doc.setFont(undefined, 'bold');

const headers = ['Date', 'Food Name', 'Type', 'Category', 'Price', 'Ingredients', 'Branch'];
let xPos = margin;

// Draw header background
doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

// Draw header text
headers.forEach((header, index) => {
    const cellX = xPos + 2;
    doc.text(header, cellX, currentY + 7);
    xPos += colWidths[index];
});

currentY += rowHeight;

// Draw table rows
doc.setFont(undefined, 'normal');
doc.setTextColor(0, 0, 0); // Black text

data.forEach((row, rowIndex) => {
    // Calculate needed height for this row
    let maxLines = 1;
    const cellLines = row.map((cell, cellIndex) => {
    const text = String(cell);
    const maxWidth = colWidths[cellIndex] - 4;
    const lines = splitTextIntoLines(doc, text, maxWidth);
    maxLines = Math.max(maxLines, lines.length);
    return lines;
    });

    const actualRowHeight = rowHeight * maxLines;

    // Check for page break (using pageHeight for landscape)
    if (currentY + actualRowHeight > pageHeight - 30) {
    doc.addPage('l'); // Add landscape page
    currentY = margin;
    
    // Redraw header on new page
    doc.setFillColor(25, 118, 210);
    doc.setTextColor(255, 255, 255);
    doc.setFont(undefined, 'bold');
    
    doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
    
    xPos = margin;
    headers.forEach((header, index) => {
        doc.text(header, xPos + 2, currentY + 7);
        xPos += colWidths[index];
    });
    
    currentY += rowHeight;
    doc.setFont(undefined, 'normal');
    doc.setTextColor(0, 0, 0);
    }
    
    // Alternate row colors
    if (rowIndex % 2 === 0) {
    doc.setFillColor(248, 248, 248); // Light gray
    } else {
    doc.setFillColor(255, 255, 255); // White
    }
    
    // Draw row background
    doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
    
    // Draw cell borders
    doc.setDrawColor(200, 200, 200);
    doc.setLineWidth(0.1);
    
    // Draw vertical lines
    let verticalX = margin;
    for (let i = 0; i <= colWidths.length; i++) {
    doc.line(verticalX, currentY, verticalX, currentY + actualRowHeight);
    if (i < colWidths.length) verticalX += colWidths[i];
    }
    
    // Draw horizontal lines
    doc.line(margin, currentY, pageWidth - margin, currentY);
    doc.line(margin, currentY + actualRowHeight, pageWidth - margin, currentY + actualRowHeight);
    
    // Draw row text with multi-line support
    cellLines.forEach((lines, cellIndex) => {
    xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
    
    lines.forEach((line, lineIndex) => {
        const lineY = currentY + 7 + (lineIndex * rowHeight);
        doc.text(line, xPos + 2, lineY);
    });
    });
    
    currentY += actualRowHeight;
});

return currentY;
};

// Export to PDF with specified columns only (using manual table like Shift service) - UPDATED FOR LANDSCAPE
export const exportToPDF = async (data, filters = {}, referenceData) => {
return new Promise((resolve, reject) => {
    try {
    // Create PDF in landscape mode
    const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
    });
    
    const pageWidth = doc.internal.pageSize.getWidth(); // This will be ~297mm in landscape
    const pageHeight = doc.internal.pageSize.getHeight(); // This will be ~210mm in landscape
    const margin = 20;
    let currentY = margin;
    
    // Title
    doc.setFontSize(20);
    doc.setTextColor(25, 118, 210);
    doc.text("Food Items Report", pageWidth / 2, currentY, { align: 'center' });
    currentY += 15;
    
    // Date and time
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, margin, currentY);
    currentY += 8;
    
    // Summary
    doc.setFontSize(11);
    doc.setTextColor(60, 60, 60);
    doc.text(`Total Items: ${data.length}`, margin, currentY);
    currentY += 8;
    
    // Apply filters text
    const filterTexts = [];
    if (filters.name) filterTexts.push(`Name: ${filters.name}`);
    if (filters.food_type_id) filterTexts.push(`Type: ${getFoodTypeById(filters.food_type_id)}`);
    if (filters.food_category_id) filterTexts.push(`Category: ${getCategoriesNameById(filters.food_category_id, referenceData?.categoryNames || [])}`);
    if (filters.dateFrom) filterTexts.push(`From: ${new Date(filters.dateFrom).toLocaleDateString()}`);
    if (filters.dateTo) filterTexts.push(`To: ${new Date(filters.dateTo).toLocaleDateString()}`);
    if (filters.searchTerm) filterTexts.push(`Search: ${filters.searchTerm}`);
    
    if (filterTexts.length > 0) {
        doc.setFontSize(9);
        doc.setTextColor(80, 80, 80);
        const filtersText = `Filters: ${filterTexts.join(', ')}`;
        const filtersLines = splitTextIntoLines(doc, filtersText, pageWidth - 2 * margin);
        
        filtersLines.forEach(line => {
        if (currentY > pageHeight - 40) { // Using pageHeight for landscape check
            doc.addPage('l'); // Add landscape page
            currentY = margin;
        }
        doc.text(line, margin, currentY);
        currentY += 6;
        });
        
        currentY += 10;
    }
    
    // Prepare table data with selected columns only
    const tableData = data.map(item => [
        formatCreatedAtDate(item.created_at),
        item.name,
        getFoodTypeById(item.food_type_id) || 'N/A',
        getCategoriesNameById(item.food_category_id, referenceData?.categoryNames || []) || 'N/A',
        `$${formatNumberWithComma(item.price)}`,
        String(item.ingredients?.length || 0),
        getCategoriesNameById(item.branch_id, referenceData?.branchNames || []) || 'N/A'
    ]);
    
    // Draw the table
    drawFoodTable(doc, tableData, currentY + 10, referenceData);
    
    // Add page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${i} of ${pageCount}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    }
    
    const fileName = `food_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
    resolve();
    } catch (error) {
    reject(error);
    }
});
};